// WbProductCombobox — searchable product picker for a ticket line row.
//
// Replaces the per-line native <select>. The product catalogue is bounded
// (useWbProducts caps at per_page=100), so filtering is client-side on name OR
// code — no server round-trip per keystroke. Keyboard-navigable: ArrowUp/Down
// move the highlight, Enter confirms, Escape closes. Click-outside closes via a
// useRef on the root.
//
// Read-only mode (ticket completed / paid) renders the product name as plain
// text instead of a combobox.
//
// Mirrors the prop shape of the sibling pickers (value = the stable scalar id
// + an onChange that fires the new id or null). `selectedNameFallback` covers a
// hydrated line whose product isn't in the loaded catalogue (deactivated, or
// beyond the page) — and whose enc_id ciphertext won't match any option,
// because entity enc_ids re-encrypt with a random IV on every server response.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

import type { WbProduct } from '../../hooks/useWbProducts';
import type { WbProductGroup } from '../../hooks/useWbProductGroups';
import styles from './WbProductCombobox.module.css';

export interface WbProductComboboxProps {
  readonly products: ReadonlyArray<WbProduct>;
  /** Currently-selected product_enc_id (or null). */
  readonly value: string | null;
  readonly onChange: (encId: string | null) => void;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  /** Name to show when `value` is set but not present in `products`. */
  readonly selectedNameFallback?: string | null;
  readonly ariaLabel?: string;
  readonly 'data-testid'?: string;
  /**
   * When provided and non-empty, the dropdown options are grouped under their
   * product group's name (sorted by group sort_order, then name). Products with
   * no group — or whose group is not in this list (e.g. excluded) — fall into a
   * trailing "Ungrouped" section. Omit for the legacy flat list.
   */
  readonly groups?: ReadonlyArray<WbProductGroup>;
}

interface ComboSection {
  readonly key: string;
  readonly label: string | null;
  readonly products: ReadonlyArray<WbProduct>;
}

export function WbProductCombobox({
  products,
  value,
  onChange,
  disabled = false,
  placeholder = 'Search products…',
  selectedNameFallback = null,
  ariaLabel = 'Product',
  'data-testid': testId,
  groups,
}: WbProductComboboxProps): JSX.Element {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef<HTMLDivElement | null>(null);

  const selected = useMemo(
    () => (value !== null ? products.find((p) => p.enc_id === value) ?? null : null),
    [products, value],
  );
  const selectedName =
    selected?.name ??
    (value !== null ? selectedNameFallback ?? 'Selected product' : null);

  // Filter the catalogue by name OR code while the dropdown is open and the
  // user is typing. An empty query shows the whole (bounded) list.
  const filtered = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (q === '') return products.slice(0, 100);
    return products
      .filter(
        (p) =>
          p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q),
      )
      .slice(0, 100);
  }, [products, text]);

  // Group the filtered products into sections when a non-empty `groups` list is
  // supplied. Sections are ordered by group sort_order then name; products
  // within a section and the trailing "Ungrouped" bucket are sorted by name. A
  // product whose group is absent here (excluded, or none) falls into Ungrouped.
  const sections = useMemo<ComboSection[] | null>(() => {
    const active = (groups ?? []).filter((g) => !g.is_excluded);
    if (active.length === 0) return null;
    const byCode = new Map(active.map((g) => [g.code, g]));
    const grouped = new Map<string, WbProduct[]>();
    const ungrouped: WbProduct[] = [];
    for (const p of filtered) {
      const code = p.group !== null && byCode.has(p.group.code) ? p.group.code : null;
      if (code === null) {
        ungrouped.push(p);
      } else {
        const arr = grouped.get(code) ?? [];
        arr.push(p);
        grouped.set(code, arr);
      }
    }
    const byName = (a: WbProduct, b: WbProduct) => a.name.localeCompare(b.name);
    const ordered = [...active].sort(
      (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name),
    );
    const result: ComboSection[] = [];
    for (const g of ordered) {
      const ps = grouped.get(g.code);
      if (ps && ps.length > 0) {
        result.push({ key: g.code, label: g.name, products: [...ps].sort(byName) });
      }
    }
    if (ungrouped.length > 0) {
      result.push({ key: '__ungrouped__', label: 'Ungrouped', products: [...ungrouped].sort(byName) });
    }
    return result;
  }, [filtered, groups]);

  // Flat list that matches the visual order — the source of truth for keyboard
  // navigation and Enter selection (so activeIndex aligns with what's rendered).
  const ordered = useMemo<ReadonlyArray<WbProduct>>(
    () => (sections === null ? filtered : sections.flatMap((s) => s.products)),
    [sections, filtered],
  );

  // Keep the highlight in range as the filtered set changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [text]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    function handle(e: MouseEvent) {
      if (!rootRef.current) return;
      if (e.target instanceof Node && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  function select(p: WbProduct) {
    onChange(p.enc_id);
    setText('');
    setOpen(false);
  }

  function clear() {
    onChange(null);
    setText('');
    setOpen(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, ordered.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      const pick = ordered[activeIndex];
      if (open && pick) {
        e.preventDefault();
        select(pick);
      }
    }
  }

  // Read-only: plain text, no combobox affordances.
  if (disabled) {
    return (
      <span className={styles.readonly} data-testid={testId} aria-label={ariaLabel}>
        {selectedName ?? '—'}
      </span>
    );
  }

  // While a product is selected and the user isn't actively typing, the input
  // shows the product name; typing switches it to search mode. Keyed on `value`
  // (not `selected`) so a selection whose product is absent from the loaded
  // catalogue still shows its fallback name rather than blanking.
  const displayValue = value !== null && text === '' ? selectedName ?? '' : text;

  function renderOption(p: WbProduct, idx: number): JSX.Element {
    return (
      <button
        key={p.enc_id}
        type="button"
        role="option"
        aria-selected={p.enc_id === value}
        className={`${styles.option} ${idx === activeIndex ? styles.optionActive : ''}`}
        // mousedown (not click) so the selection lands before the input's
        // blur/outside-click handler can close the dropdown.
        onMouseDown={(e) => {
          e.preventDefault();
          select(p);
        }}
        onMouseEnter={() => setActiveIndex(idx)}
      >
        {p.name}
        <span className={styles.optionCode}>{p.code}</span>
      </button>
    );
  }

  return (
    <div ref={rootRef} className={styles.root} data-testid={testId}>
      <div className={styles.inputRow}>
        <input
          className={styles.input}
          type="text"
          role="combobox"
          value={displayValue}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            // Typing while a product is selected clears the selection — the
            // operator has signalled intent to re-pick.
            if (value !== null) onChange(null);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-autocomplete="list"
          aria-expanded={open}
          autoComplete="off"
        />
        {value !== null ? (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={clear}
            aria-label="Clear product"
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            ✕
          </button>
        ) : null}
      </div>

      {open ? (
        <div className={styles.dropdown} role="listbox" aria-label={`${ariaLabel} options`}>
          {ordered.length === 0 ? (
            <div className={styles.dropdownStatus}>No products match.</div>
          ) : sections === null ? (
            ordered.map((p, idx) => renderOption(p, idx))
          ) : (
            (() => {
              // Running flat index so each option's highlight aligns with
              // activeIndex / the `ordered` list driving keyboard nav.
              let flatIdx = 0;
              return sections.map((section) => (
                <div key={section.key} role="group" aria-label={section.label ?? undefined}>
                  {section.label !== null ? (
                    <div className={styles.groupHeader}>{section.label}</div>
                  ) : null}
                  {section.products.map((p) => renderOption(p, flatIdx++))}
                </div>
              ));
            })()
          )}
        </div>
      ) : null}
    </div>
  );
}
